import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../layouts/Header';
import { Button, Card, Col, Form, Row, Spinner, Table, Badge, Pagination } from 'react-bootstrap';
import { ArrowLeft, CurrencyRupee } from 'react-bootstrap-icons';
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import OutletSelect from '../../components/OutletSelect';

const JourneyRevenueReport = () => {
    const accessToken = localStorage.getItem("accessToken");
    const [skin, setSkin] = useState(localStorage.getItem("skin-mode") || "light");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [outlets, setOutlets] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [clicked, setClicked] = useState(false);
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const navigate = useNavigate();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
        }),
    };

    const outletOptions = outlets.map((outlet) => ({
        value: outlet._id,
        label: `${outlet.name} (${outlet.city})`,
    }));

    // Format currency in Indian Rupees
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                const filteredOutlets = response?.data?.data?.outlets.filter(
                    (act) => act?.status === "ACTIVE" || act?.status === "INACTIVE"
                );
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            getRevenueReport(selectedOutlet.value, startDate, endDate, page, recordsPerPage);
        }
    };

    const getRevenueReport = async (outlet, sDate, eDate, page, limit) => {
        if (!selectedOutlet || !startDate || !endDate) {
            toast.error("Please select outlet, start date, and end date.");
            return;
        }

        setSubBtnLoading(true);
        setClicked(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            // Format dates as DD-MM-YYYY
            const formatDateString = (date) => {
                const d = new Date(date);
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                return `${year}-${month}-${day}`;
            };

            const startDateStr = formatDateString(sDate);
            const endDateStr = formatDateString(eDate);

            const response = await axios.get(
                `/auto-coupon-allocation/getAutoCouponUsageSummary?startDate=${startDateStr}&endDate=${endDateStr}&outlet=${outlet}&page=${page}&limit=${limit}`,
                { headers }
            );

            console.log("Revenue Report Response:", response);

            if (response?.data?.status === true) {
                setReportData(response.data.data || []);
                setTotalPages(response?.data?.pagination?.totalPages || 1);
                setTotalRecords(response?.data?.pagination?.totalRecords || 0);
                toast.success("Revenue report generated successfully!");
            } else {
                setReportData([]);
                toast.info("No data found for the selected criteria.");
            }
        } catch (error) {
            console.log("Error in fetching revenue report:", error);
            handleApiError(error);
            setReportData([]);
        } finally {
            setSubBtnLoading(false);
        }
    };

    const handleApiError = (error) => {
        let errorMessage = 'An error occurred';
        if (error.response?.status === 406) {
            errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
        } else if (error.response?.status === 412) {
            errorMessage = error.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
            errorMessage = error.response?.data?.error;
        }

        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCurrentPage(1);
        getRevenueReport(selectedOutlet.value, startDate, endDate, 1, recordsPerPage);
    };

    const handleRefresh = async () => {
        if (selectedOutlet && startDate && endDate) {
            getRevenueReport(selectedOutlet.value, startDate, endDate, currentPage, recordsPerPage);
        } else {
            toast.error("Please select outlet, start date, and end date to refresh.");
        }
    };

    // Calculate totals for summary
    const calculateTotals = () => {
        const totals = reportData.reduce(
            (acc, item) => {
                acc.totalAllocatedUsers += item.totalAllocatedUsers || 0;
                acc.totalUsedUsers += item.totalUsedUsers || 0;
                acc.totalGrandTotal += item.totalGrandTotal || 0;
                acc.totalTaxAmount += item.totalTaxAmount || 0;
                acc.totalTaxable += item.totalTaxable || 0;
                acc.totalDiscount += item.totalDiscount || 0;
                return acc;
            },
            {
                totalAllocatedUsers: 0,
                totalUsedUsers: 0,
                totalGrandTotal: 0,
                totalTaxAmount: 0,
                totalTaxable: 0,
                totalDiscount: 0
            }
        );

        totals.totalUnusedUsers = totals.totalAllocatedUsers - totals.totalUsedUsers;
        totals.netRevenue = totals.totalGrandTotal - totals.totalTaxAmount;

        return totals;
    };

    const renderPaginationItems = () => {
        if (totalPages > 15) {
            return (
                <>
                    {currentPage > 3 && (
                        <Pagination.Item onClick={() => handlePageChange(1)}>
                            1
                        </Pagination.Item>
                    )}
                    {currentPage > 4 && <Pagination.Ellipsis />}

                    {Array.from({ length: 5 }, (_, i) => {
                        const pageNumber = currentPage > 3 ? currentPage - 2 + i : i + 1;
                        if (pageNumber <= totalPages) {
                            return (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            );
                        }
                        return null;
                    })}

                    {currentPage < totalPages - 3 && <Pagination.Ellipsis />}
                    {currentPage < totalPages - 2 && (
                        <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                            {totalPages}
                        </Pagination.Item>
                    )}
                </>
            );
        } else {
            return Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => handlePageChange(i + 1)}
                >
                    {i + 1}
                </Pagination.Item>
            ));
        }
    };

    // useEffect(() => {
    //     const now = new Date();
    //     const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    //     const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    //     setStartDate(firstOfMonth);
    //     setEndDate(currentDate);
    // }, []);

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        setStartDate(firstOfMonth);
        setEndDate(yesterday);
    }, []);

    useEffect(() => {
        getOutlets();
    }, []);

    useEffect(() => {
        if (clicked && selectedOutlet && startDate && endDate) {
            getRevenueReport(selectedOutlet.value, startDate, endDate, 1, recordsPerPage);
        }
    }, [recordsPerPage]);

    const totals = calculateTotals();

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0">
                        <div className="card-header bg-white border-0 py-1 d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-0 fw-bold d-inline-block">
                                    <i className="bi bi-graph-up me-2"></i>JOURNEY COUPON REVENUE REPORT
                                </h4>
                            </div>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate('/reports/all-reports')}
                                    className="me-3 d-flex align-items-center fw-semibold"
                                    style={{ height: '38px' }}
                                >
                                    <ArrowLeft className="me-1" /> Back to Reports
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <hr className='mt-0 mb-0' />

                    <Card.Body>
                        {/* Filters Section */}
                        <div className="p-3 bg-light rounded-3 mb-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4} lg={4} sm={3}>
                                        <Form.Group controlId="outletSelect">
                                            <OutletSelect
                                                value={selectedOutlet}
                                                required={true}
                                                onChange={(option) => setSelectedOutlet(option)}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} lg={2} sm={3}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Start Date"
                                                className="form-control border-secondary"
                                                // maxDate={endDate}
                                                maxDate={yesterday}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} lg={2} sm={3}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-calendar me-2"></i>End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                                maxDate={yesterday}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} lg={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            type="submit"
                                            disabled={subBtnLoading || !selectedOutlet || !startDate || !endDate}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={2} lg={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            onClick={handleRefresh}
                                            disabled={!selectedOutlet || !startDate || !endDate || subBtnLoading}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Refresh"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* Report Table Section */}
                        {clicked && (
                            <>
                                {reportData && reportData.length > 0 ? (
                                    <>
                                        {/* Summary Cards */}
                                        <Row className="mb-4">
                                            <Col md={3}>
                                                <Card className="bg-primary text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Total Coupons</h6>
                                                                <h3 className="mb-0 fw-semibold">{totals.totalAllocatedUsers}</h3>
                                                                <small>Used: {totals.totalUsedUsers} 
                                                                {/* | Unused: {totals.totalUnusedUsers} */}
                                                                </small>
                                                            </div>
                                                            <i className="bi bi-ticket-perforated fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="bg-success text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Grand Total</h6>
                                                                <small className='text-success'>.</small>
                                                                <h3 className="mb-0 fw-semibold">{formatCurrency(totals.totalGrandTotal)}</h3>
                                                            </div>
                                                            <CurrencyRupee className="fs-1 opacity-50" />
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="bg-info text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Taxable Amount</h6>
                                                                <h3 className="mb-0 fw-semibold">{formatCurrency(totals.totalTaxable)}</h3>
                                                                <small>Tax: {formatCurrency(totals.totalTaxAmount)}</small>
                                                            </div>
                                                            <i className="bi bi-percent fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={3}>
                                                <Card className="bg-warning text-white shadow-sm">
                                                    <Card.Body className="py-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <h6 className="mb-0">Total Discount</h6>
                                                                <h3 className="mb-0 fw-semibold">{formatCurrency(totals.totalDiscount)}</h3>
                                                                <small>Net: {formatCurrency(totals.netRevenue)}</small>
                                                            </div>
                                                            <i className="bi bi-tags fs-1 opacity-50"></i>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Pagination Controls - Top */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center">
                                                <span className="me-2 text-muted">Show</span>
                                                <Form.Select
                                                    style={{ width: "auto" }}
                                                    value={recordsPerPage}
                                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                                    className="border-secondary"
                                                >
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                    <option value={200}>200</option>
                                                </Form.Select>
                                                <span className="ms-2 text-muted">entries</span>
                                            </div>
                                            <Pagination className="mb-0">
                                                <Pagination.Prev
                                                    disabled={currentPage === 1}
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                />
                                                {renderPaginationItems()}
                                                <Pagination.Next
                                                    disabled={currentPage === totalPages}
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                />
                                            </Pagination>
                                        </div>

                                        {/* Table */}
                                        <div className="table-responsive">
                                            <Table striped bordered hover className="align-middle">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="text-center" style={{ width: '60px' }}>#</th>
                                                        <th>Date</th>
                                                        <th>Coupon Title</th>
                                                        <th>Order #</th>
                                                        <th className="text-center">Allocated</th>
                                                        <th className="text-center">Used</th>
                                                        {/* <th className="text-center">Unused</th> */}
                                                        <th className="text-end">Taxable</th>
                                                        <th className="text-end">Tax</th>
                                                        <th className="text-end">Discount</th>
                                                        <th className="text-end">Grand Total</th>
                                                        {/* <th className="text-center">Usage Rate</th> */}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.map((item, index) => {
                                                        const usageRate = item.totalAllocatedUsers > 0
                                                            ? ((item.totalUsedUsers / item.totalAllocatedUsers) * 100).toFixed(2)
                                                            : '0.00';

                                                        const date = new Date(item.startDate).toLocaleDateString('en-IN', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        });

                                                        return (
                                                            <tr key={item._id || index}>
                                                                <td className="text-center">{((currentPage - 1) * recordsPerPage) + index + 1}</td>
                                                                <td>{date}</td>
                                                                <td >
                                                                    {item.autoCouponAllocation?.title || 'N/A'}
                                                                    {item.autoCouponAllocation?.discountType && (
                                                                        <Badge bg="light" text="dark" className="ms-2">
                                                                            {item.autoCouponAllocation.discountType}
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    {item.autoCouponAllocation?.nthOrderNumber ? (
                                                                        <Badge bg="info">{item.autoCouponAllocation.nthOrderNumber}th</Badge>
                                                                    ) : (
                                                                        <Badge bg="secondary">Any</Badge>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">{item.totalAllocatedUsers || 0}</td>
                                                                <td className="text-center text-success fw-bold">{item.totalUsedUsers || 0}</td>
                                                                {/* <td className="text-center text-warning fw-bold">{item.totalUnusedUsers || 0}</td> */}
                                                                <td className="text-end">{formatCurrency(item.totalTaxable)}</td>
                                                                <td className="text-end">{formatCurrency(item.totalTaxAmount)}</td>
                                                                <td className="text-end text-danger fw-semibold">{formatCurrency(item.totalDiscount)}</td>
                                                                <td className="text-end fw-semibold">{formatCurrency(item.totalGrandTotal)}</td>
                                                                {/* <td className="text-center">
                                                                    <Badge bg={usageRate >= 50 ? 'success' : usageRate > 0 ? 'warning' : 'danger'}>
                                                                        {usageRate}%
                                                                    </Badge>
                                                                </td> */}
                                                            </tr>
                                                        );
                                                    })}
                                                    {/* Summary Row */}
                                                    <tr className="fw-bold bg-light">
                                                        <td colSpan="4" className="text-end">TOTAL:</td>
                                                        <td className="text-center">{totals.totalAllocatedUsers}</td>
                                                        <td className="text-center text-success">{totals.totalUsedUsers}</td>
                                                        {/* <td className="text-center text-warning">{totals.totalUnusedUsers}</td> */}
                                                        <td className="text-end">{formatCurrency(totals.totalTaxable)}</td>
                                                        <td className="text-end">{formatCurrency(totals.totalTaxAmount)}</td>
                                                        <td className="text-end text-danger">{formatCurrency(totals.totalDiscount)}</td>
                                                        <td className="text-end">{formatCurrency(totals.totalGrandTotal)}</td>

                                                        {/* <td className="text-center">
                                                            <Badge bg={totals.totalAllocatedUsers > 0 && (totals.totalUsedUsers / totals.totalAllocatedUsers * 100) >= 50 ? 'success' : 'secondary'}>
                                                                {totals.totalAllocatedUsers > 0 ? ((totals.totalUsedUsers / totals.totalAllocatedUsers) * 100).toFixed(2) : '0.00'}%
                                                            </Badge>
                                                        </td> */}
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Pagination Info */}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="text-muted">
                                                Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} entries
                                            </div>
                                            <Pagination className="mb-0">
                                                <Pagination.Prev
                                                    disabled={currentPage === 1}
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                />
                                                {renderPaginationItems()}
                                                <Pagination.Next
                                                    disabled={currentPage === totalPages}
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                />
                                            </Pagination>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-inbox fs-1 text-muted"></i>
                                        <h5 className="mt-3 text-muted">No revenue data available</h5>
                                        <p className="text-muted">Please select outlet and date range to generate report</p>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    );
};

export default JourneyRevenueReport;